import { CheckMarkIcon, ChevronUpDownIcon } from "/icons";
import { getDynamicText } from "/utils";
import { ProductBlocksSubscription_selector } from "_types";
import clsx from "clsx";
import { ReactProductState } from "product";
import React, { FC, useState } from "react";
import { StoreApi, UseBoundStore } from "zustand";

export const ProductSubscriptionSelector: FC<{
  settings: ProductBlocksSubscription_selector["settings"];
  useProduct: UseBoundStore<StoreApi<ReactProductState>>;
}> = ({ settings, useProduct }) => {
  const { product, setSelectedSellingPlan, selectedSellingPlan, selectedVariant } = useProduct(
    ({ product, selectedVariant, setSelectedSellingPlan, selectedSellingPlan }) => ({
      product,
      selectedVariant,
      setSelectedSellingPlan,
      selectedSellingPlan,
    })
  );

  if (!product?.variants.some((v) => v.selling_plan_allocations?.length)) {
    return null;
  }

  return (
    <div className="grid gap-[--product-grid-gap]">
      <fieldset
        className={clsx(
          "grid gap-[--product-grid-gap]",
          settings.mobile_display === "row" ? "grid-cols-2" : "md:grid-cols-2"
        )}
      >
        {product?.requires_selling_plan && selectedVariant.selling_plan_allocations?.length
          ? <button
              type="button"
              className={clsx(
                "group flex cursor-pointer !gap-1 !px-4 py-3 text-left text-xs font-semibold normal-case md:!gap-2",
                settings.button__size,
                selectedSellingPlan ? "button-primary" : "button-primary--outline",
                settings.mobile_display === "row" ? "col-span-2" : "md:col-span-2"
              )}
              onClick={() => {
                setSelectedSellingPlan(
                  selectedSellingPlan ?? selectedVariant?.selling_plan_allocations[0]
                );
              }}
            >
              <span
                className={clsx(
                  "flex h-4 w-4 shrink-0 items-center justify-center overflow-hidden rounded-full border-2 md:h-5 md:w-5",
                  "border-primary-text bg-primary-text text-primary-bg"
                )}
              >
                <CheckMarkIcon className="h-2 w-2 stroke-2 md:h-2.5 md:w-2.5" />
              </span>
              <span
                className="flex-1"
                dangerouslySetInnerHTML={{
                  __html:
                    getDynamicText(settings.subscription_text, {
                      product,
                      selectedVariant,
                    }) ||
                    product?.selling_plan_groups.find(
                      (group) => group.id === selectedSellingPlan?.group_id
                    )?.name,
                }}
              ></span>
            </button>
          : <>
              <button
                type="button"
                className={clsx(
                  "group flex cursor-pointer !gap-1 !px-4 py-3 text-left text-xs font-semibold normal-case md:!gap-2",
                  settings.button__size,
                  !selectedSellingPlan ? "button-primary" : "button-primary--outline"
                )}
                onClick={(e) => {
                  setSelectedSellingPlan(null);
                }}
              >
                <span
                  className={clsx(
                    "flex h-4 w-4 shrink-0 items-center justify-center overflow-hidden rounded-full border-2 md:h-5 md:w-5",
                    !selectedSellingPlan
                      ? "border-primary-text bg-primary-text text-primary-bg"
                      : "border-primary-outline group-hf:border-primary-text"
                  )}
                >
                  {!selectedSellingPlan
                    ? <CheckMarkIcon className="h-2 w-2 stroke-2 md:h-2.5 md:w-2.5" />
                    : null}
                </span>
                <span
                  className="flex-1"
                  dangerouslySetInnerHTML={{
                    __html:
                      getDynamicText(settings.no_subscription_text, { product, selectedVariant }) ||
                      "One-time Purchase",
                  }}
                ></span>
              </button>
              <button
                type="button"
                className={clsx(
                  "group flex cursor-pointer !gap-1 !px-4 py-3 text-left text-xs font-semibold normal-case md:!gap-2",
                  settings.button__size,
                  selectedSellingPlan ? "button-primary" : "button-primary--outline"
                )}
                onClick={(e) => {
                  setSelectedSellingPlan(
                    selectedSellingPlan ?? selectedVariant?.selling_plan_allocations[0]
                  );
                }}
              >
                <span
                  className={clsx(
                    "flex h-4 w-4 shrink-0 items-center justify-center overflow-hidden rounded-full border-2 md:h-5 md:w-5",
                    "border-primary-bg bg-primary-text text-primary-bg"
                  )}
                >
                  {selectedSellingPlan
                    ? <CheckMarkIcon className="h-2 w-2 stroke-2 md:h-2.5 md:w-2.5" />
                    : null}
                </span>
                <span
                  className="flex-1"
                  dangerouslySetInnerHTML={{
                    __html:
                      getDynamicText(settings.subscription_text, {
                        product,
                        selectedVariant,
                      }) ||
                      product?.selling_plan_groups.find(
                        (group) => group.id === selectedSellingPlan?.group_id
                      )?.name,
                  }}
                ></span>
              </button>
            </>}
      </fieldset>
      {selectedSellingPlan
        ? <label className="relative">
            <span className="sr-only text-sm font-semibold uppercase">
              {selectedSellingPlan?.name}
            </span>
            <div className="relative w-full p-0">
              <select
                className="select-input--md font-button grid w-full appearance-none !pr-6"
                onChange={(e) => {
                  const newSellingPlanId = (e.target as HTMLSelectElement)?.value;
                  setSelectedSellingPlan(
                    selectedVariant.selling_plan_allocations?.find(
                      (plan) => plan.id === +newSellingPlanId
                    )
                  );
                }}
                value={selectedSellingPlan?.id}
              >
                {selectedVariant?.selling_plan_allocations?.map((sellingPlan) => {
                  return (
                    <option
                      key={sellingPlan?.id}
                      value={sellingPlan?.id}
                      className="disabled:text-theme-text/50"
                      selected={selectedSellingPlan?.id === sellingPlan?.id}
                    >
                      {sellingPlan?.name}
                    </option>
                  );
                })}
              </select>
              <div className="pointer-events-none absolute right-2 top-0 flex h-full items-center justify-center">
                <ChevronUpDownIcon className="h-5 w-5" />
              </div>
            </div>
          </label>
        : null}
    </div>
  );
};
